import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subscription, Subject } from 'rxjs';
import { AfpBatchService } from './afp-batch.service';

export interface BackgroundJob {
  id: string;
  batchId: string;
  type: 'bulk-invoice' | 'partial-invoice' | 'full-invoice';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_jobs: number;
  processed_jobs: number;
  successful_jobs: number;
  failed_jobs: number;
  error_details: any[];
  startedAt: Date;
  completedAt?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class BackgroundJobsService {
  private jobs = new BehaviorSubject<BackgroundJob[]>([]);
  public jobs$ = this.jobs.asObservable();
  
  // Event emitter for job completion
  private jobCompleted = new Subject<BackgroundJob>();
  public jobCompleted$ = this.jobCompleted.asObservable();
  
  private pollingSubscriptions: Map<string, Subscription> = new Map();

  constructor(private afpBatchService: AfpBatchService) {
    // Load jobs from localStorage on init
    this.loadJobsFromStorage();
  }

  addJob(job: BackgroundJob) {
    const currentJobs = this.jobs.value;
    const updatedJobs = [...currentJobs, job];
    this.jobs.next(updatedJobs);
    this.saveJobsToStorage();
    
    // Start polling for this job
    this.startPolling(job.id);
  }

  updateJob(jobId: string, updates: Partial<BackgroundJob>) {
    const currentJobs = this.jobs.value;
    const updatedJobs = currentJobs.map(job => 
      job.id === jobId ? { ...job, ...updates } : job
    );
    this.jobs.next(updatedJobs);
    this.saveJobsToStorage();
  }

  removeJob(jobId: string) {
    const currentJobs = this.jobs.value;
    const updatedJobs = currentJobs.filter(job => job.id !== jobId);
    this.jobs.next(updatedJobs);
    this.saveJobsToStorage();
    
    // Stop polling for this job
    this.stopPolling(jobId);
  }

  getActiveJobsCount(): number {
    return this.jobs.value.filter(job => 
      job.status === 'pending' || job.status === 'processing'
    ).length;
  }

  getCompletedJobsCount(): number {
    return this.jobs.value.filter(job => 
      job.status === 'completed' || job.status === 'failed'
    ).length;
  }

  clearCompletedJobs() {
    const currentJobs = this.jobs.value;
    const activeJobs = currentJobs.filter(job => 
      job.status === 'pending' || job.status === 'processing'
    );
    this.jobs.next(activeJobs);
    this.saveJobsToStorage();
  }

  private startPolling(jobId: string) {
    // Poll every 3 seconds
    const subscription = interval(3000).subscribe(() => {
      this.checkJobStatus(jobId);
    });
    
    this.pollingSubscriptions.set(jobId, subscription);
    
    // Check immediately
    this.checkJobStatus(jobId);
  }

  private stopPolling(jobId: string) {
    const subscription = this.pollingSubscriptions.get(jobId);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingSubscriptions.delete(jobId);
    }
  }

  private checkJobStatus(jobId: string) {
    const job = this.jobs.value.find(j => j.id === jobId);
    if (!job) return;

    this.afpBatchService.getBulkInvoiceStatus(jobId).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          const updates: Partial<BackgroundJob> = {
            status: res.data.status,
            processed_jobs: res.data.processed_jobs || 0,
            successful_jobs: res.data.successful_jobs || 0,
            failed_jobs: res.data.failed_jobs || 0,
            error_details: res.data.error_details || []
          };

          // If completed or failed, stop polling
          if (res.data.status === 'completed' || res.data.status === 'failed') {
            updates.completedAt = new Date();
            this.stopPolling(jobId);
            
            // Emit completion event
            const updatedJob = { ...job, ...updates };
            this.jobCompleted.next(updatedJob);
          }

          this.updateJob(jobId, updates);
        }
      },
      error: (err) => {
        console.error('Error checking job status', err);
        const failedJob = { ...job, status: 'failed' as const, completedAt: new Date() };
        this.updateJob(jobId, { status: 'failed' });
        this.stopPolling(jobId);
        
        // Emit completion event for failed job
        this.jobCompleted.next(failedJob);
      }
    });
  }

  private saveJobsToStorage() {
    try {
      localStorage.setItem('background_jobs', JSON.stringify(this.jobs.value));
    } catch (e) {
      console.error('Failed to save jobs to storage', e);
    }
  }

  private loadJobsFromStorage() {
    try {
      const stored = localStorage.getItem('background_jobs');
      if (stored) {
        const jobs = JSON.parse(stored);
        // Only load jobs from last 24 hours
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const recentJobs = jobs.filter((job: BackgroundJob) => 
          new Date(job.startedAt) > oneDayAgo
        );
        this.jobs.next(recentJobs);
        
        // Resume polling for active jobs
        recentJobs.forEach((job: BackgroundJob) => {
          if (job.status === 'pending' || job.status === 'processing') {
            this.startPolling(job.id);
          }
        });
      }
    } catch (e) {
      console.error('Failed to load jobs from storage', e);
    }
  }

  ngOnDestroy() {
    // Clean up all polling subscriptions
    this.pollingSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
